import { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputGroup from "../molecule/InputGroup";
import Button from "../atom/Button";
import { IconUser, IconLock } from "@tabler/icons-react";
import { Modal } from "@mantine/core";
import { API_BASE_URL } from "../../../config/api";

// Default TRUE now (unless explicitly disabled)
const MFA_UI_ENABLED =
  (import.meta?.env?.VITE_MFA_UI_ENABLED ?? "true").toLowerCase() === "true";

// Build a safe /api/* URL regardless of how API_BASE_URL ends
function buildApiUrl(path = "/") {
  const raw = (API_BASE_URL || "").trim();
  const baseNoSlash = raw.replace(/\/+$/, "");
  const baseWithApi = /\/api$/i.test(baseNoSlash) ? baseNoSlash : `${baseNoSlash}/api`;
  const safePath = path.startsWith("/") ? path : `/${path}`;
  return `${baseWithApi}${safePath}`;
}

// Focus helper for OTP
function focusOtp() {
  setTimeout(() => {
    const el = document.querySelector('input[name="otp"]');
    if (el) el.focus();
  }, 0);
}

// Small helper to call login
async function attemptLogin(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch {}
  return { res, data };
}

function LoginForm() {
  const navigate = useNavigate();

  // Login form state
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // MFA (login) state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otp, setOtp] = useState("");

  // MFA ENROLLMENT (modal) state
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaTempToken, setMfaTempToken] = useState("");
  const [setupError, setSetupError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [secretBase32, setSecretBase32] = useState("");
  const [setupOtp, setSetupOtp] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);

  // ------------ Handlers ------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOtpChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D+/g, "").slice(0, 6);
    setOtp(digitsOnly);
  };

  // ====== MFA SETUP (MODAL) HELPERS ======
  const openMfaSetup = async (tempToken) => {
    setMfaTempToken(tempToken || "");
    setMfaSetupOpen(true);

    setSetupError("");
    setSetupLoading(false);
    setQrDataUrl("");
    setSecretBase32("");
    setSetupOtp("");

    await startMfaSetup(tempToken);
  };

  const startMfaSetup = async (tempToken) => {
    if (!tempToken) {
      setSetupError("Session expired. Please log in again.");
      return;
    }
    setSetupLoading(true);
    setSetupError("");

    try {
      const url = buildApiUrl("/auth/mfa/setup/start");
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tempToken}`,
        },
      });

      let data = {};
      try { data = await res.json(); } catch {}

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          (res.status === 401
            ? "Session expired. Please log in again."
            : `Failed to start MFA (HTTP ${res.status})`);
        throw new Error(msg);
      }

      setQrDataUrl(data.qrDataUrl || "");
      setSecretBase32(data.secretBase32 || "");
    } catch (e) {
      setSetupError(e?.message || "Failed to start MFA setup");
    } finally {
      setSetupLoading(false);
    }
  };

  const verifyMfaSetup = async (e) => {
    e?.preventDefault?.();
    setSetupError("");

    if (!setupOtp) {
      setSetupError("Enter the 6-digit code from your authenticator app");
      return;
    }
    if (!mfaTempToken) {
      setSetupError("Session expired. Please log in again.");
      return;
    }

    setVerifyLoading(true);
    try {
      const url = buildApiUrl("/auth/mfa/setup/verify");
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${mfaTempToken}`,
        },
        body: JSON.stringify({ otp: setupOtp }),
      });

      let data = {};
      try { data = await res.json(); } catch {}

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          (res.status === 401
            ? "Session expired. Please log in again."
            : `Verification failed (HTTP ${res.status})`);
        throw new Error(msg);
      }

      // Successfully enrolled → close modal and prompt OTP on the form
      setMfaSetupOpen(false);
      setMfaRequired(true);
      setError("MFA enabled. Enter the 6-digit OTP to complete sign-in.");
      setOtp("");
      focusOtp();
    } catch (e) {
      setSetupError(e?.message || "Verification failed");
    } finally {
      setVerifyLoading(false);
    }
  };

  // ====== LOGIN SUBMIT ======
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const username = formData.username.trim();
    const password = formData.password.trim();
    if (!username || !password) {
      return setError("Username and password are required");
    }

    if (MFA_UI_ENABLED && mfaRequired && otp.length === 0) {
      return setError("Enter the 6-digit code from your authenticator app");
    }

    setIsLoading(true);
    try {
      // IMPORTANT: MFA safe (no /login fallback)
      const loginPaths = ["/auth/login"];
      const body = { username, password };

      if (MFA_UI_ENABLED && mfaRequired) {
        body.otp = otp;
      }

      let lastErr = null;

      for (const p of loginPaths) {
        const url = buildApiUrl(p);
        console.log("[LOGIN] hitting:", url);

        const { res, data } = await attemptLogin(url, body);

        if (res.status === 401 && (data?.mfaSetupRequired || data?.mfaRequired)) {
          if (MFA_UI_ENABLED) {
            if (data?.mfaSetupRequired) {
              await openMfaSetup(data.tempToken || "");
              return;
            }
            if (data?.mfaRequired) {
              setMfaRequired(true);
              setError(data?.message || "Multi-Factor Authentication required");
              focusOtp();
              return;
            }
          }
          lastErr = new Error("MFA is required");
          continue;
        }

        if (!res.ok || !data?.token) {
          const msg =
            data?.message ||
            data?.error ||
            (res.status === 401 ? "Invalid credentials" : `Login failed (HTTP ${res.status})`);
          lastErr = new Error(msg);
          continue;
        }

        if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("token", data.token);

        // reset MFA UI/state
        setMfaRequired(false);
        setOtp("");
        setMfaSetupOpen(false);
        setMfaTempToken("");
        setSetupOtp("");
        setSetupError("");

        navigate("/dashboard");
        return;
      }

      if (lastErr) throw lastErr;
      throw new Error("Login failed");
    } catch (err) {
      setError(err?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <h1 className="mb-2 text-2xl font-semibold">MTN Recruitment App</h1>
      <p className="mb-6 text-gray-500">Welcome to MTN Recruitment App</p>

      {error && <div className="p-3 text-sm text-red-600 bg-red-100 rounded">{error}</div>}

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <InputGroup
          name="username"
          placeholder="Username"
          icon={<IconUser size={16} />}
          value={formData.username}
          onChange={handleChange}
          required
        />
        <InputGroup
          name="password"
          placeholder="Password"
          icon={<IconLock size={16} />}
          type="password"
          value={formData.password}
          onChange={handleChange}
          required
        />

        {/* OTP required (only when MFA kicks in) */}
        {MFA_UI_ENABLED && mfaRequired && (
          <InputGroup
            name="otp"
            placeholder="6-digit OTP"
            icon={<IconLock size={16} />}
            value={otp}
            onChange={handleOtpChange}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
          />
        )}

        <Button
          label={
            isLoading
              ? mfaRequired && MFA_UI_ENABLED
                ? "Verifying..."
                : "Logging in..."
              : mfaRequired && MFA_UI_ENABLED
              ? "Verify & Login"
              : "Login"
          }
          type="submit"
          disabled={isLoading}
        />
      </form>

      {/* MFA SETUP MODAL */}
      {MFA_UI_ENABLED && (
        <Modal
          opened={mfaSetupOpen}
          onClose={() => setMfaSetupOpen(false)}
          centered
          title="Set up Multi-Factor Authentication"
          overlayProps={{ opacity: 0.4, blur: 1 }}
          styles={{
            title: { fontWeight: 600 },
            header: { borderBottom: "1px solid #eee" },
            body: { paddingTop: 12 },
          }}
        >
          {setupError && (
            <div className="p-3 text-sm text-red-600 bg-red-100 rounded mb-3">
              {setupError}
            </div>
          )}

          {setupLoading ? (
            <div className="text-gray-600 py-2">Loading…</div>
          ) : (
            <form onSubmit={verifyMfaSetup} className="space-y-4">
              {/* QR */}
              {qrDataUrl ? (
                <div className="flex justify-center">
                  <img
                    src={qrDataUrl}
                    alt="Scan this QR with your authenticator app"
                    className="w-56 h-56 border rounded bg-white object-contain"
                  />
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Couldn’t load QR. You can enter the secret manually in your app.
                </div>
              )}

              {/* Manual secret */}
              {secretBase32 && (
                <div className="text-xs text-gray-600">
                  Manual code: <span className="font-mono">{secretBase32}</span>
                </div>
              )}

              {/* OTP for setup verification */}
              <InputGroup
                name="setupOtp"
                placeholder="6-digit OTP"
                icon={<IconLock size={16} />}
                value={setupOtp}
                onChange={(e) => setSetupOtp(e.target.value.replace(/\D+/g, "").slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
              />

              <Button
                label={verifyLoading ? "Verifying..." : "Verify & Enable MFA"}
                type="submit"
                disabled={verifyLoading}
              />

              <div className="text-xs text-gray-500">
                After enabling MFA, return to the login form and enter a fresh OTP to sign in.
              </div>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}

export default LoginForm;
